import React, { useState, useRef, useEffect } from 'react';
import {
  Bell, Warning, X, Checks, Trash,
  CaretRight, Clock
} from '@phosphor-icons/react';
import { clearReadNotifications } from '../api';

const SEVERITY_STYLE = {
  critical: { bg: 'bg-alert-critical/10', border: 'border-alert-critical/30', text: 'text-alert-critical', dot: 'bg-alert-critical' },
  warning: { bg: 'bg-alert-warning/10', border: 'border-alert-warning/30', text: 'text-alert-warning', dot: 'bg-alert-warning' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationCenter({ notifications, unreadCount, markRead, markAllRead, refetch }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'critical') return n.severity === 'critical';
    return true;
  });

  const handleClearRead = async () => {
    try {
      await clearReadNotifications();
      refetch();
    } catch {
      // silently fail
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-background transition-colors text-text-secondary"
      >
        <Bell size={20} weight={unreadCount > 0 ? 'fill' : 'duotone'} className={unreadCount > 0 ? 'text-primary' : ''} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-alert-critical text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse-glow">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute top-full right-0 mt-2 w-[380px] max-h-[520px] bg-surface border border-border rounded-lg shadow-xl z-50 flex flex-col overflow-hidden animate-fade-in"
        >
          {/* Header */}
          <div className="p-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bell size={18} weight="fill" className="text-primary" />
                <h3 className="font-heading text-sm font-bold text-text-primary">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-alert-critical/10 text-alert-critical text-[10px] font-bold rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    data-testid="mark-all-read-button"
                    onClick={markAllRead}
                    className="p-1.5 rounded-md hover:bg-background transition-colors"
                    title="Mark all read"
                  >
                    <Checks size={16} className="text-tertiary" />
                  </button>
                )}
                <button
                  onClick={handleClearRead}
                  className="p-1.5 rounded-md hover:bg-background transition-colors"
                  title="Clear read"
                >
                  <Trash size={14} className="text-tertiary" />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="p-1.5 rounded-md hover:bg-background transition-colors"
                >
                  <X size={16} className="text-tertiary" />
                </button>
              </div>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-1 bg-background rounded-lg p-0.5">
              {[
                { id: 'all', label: 'All' },
                { id: 'unread', label: 'Unread' },
                { id: 'critical', label: 'Critical' },
              ].map(tab => (
                <button
                  key={tab.id}
                  data-testid={`notif-filter-${tab.id}`}
                  onClick={() => setFilter(tab.id)}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-body font-medium transition-all ${
                    filter === tab.id
                      ? 'bg-surface text-text-primary shadow-sm'
                      : 'text-tertiary hover:text-text-secondary'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notification list */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="text-center py-10">
                <Bell size={32} weight="duotone" className="text-border mx-auto mb-2" />
                <p className="font-body text-xs text-text-secondary">
                  {filter === 'all' ? 'No notifications yet' : `No ${filter} notifications`}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {filtered.map((notif) => {
                  const style = SEVERITY_STYLE[notif.severity] || SEVERITY_STYLE.warning;
                  return (
                    <div
                      key={notif._id}
                      data-testid={`notification-item-${notif._id}`}
                      className={`flex items-start gap-3 p-3 hover:bg-background/50 transition-colors cursor-pointer ${
                        !notif.read ? 'bg-primary/[0.03]' : ''
                      }`}
                      onClick={() => { if (!notif.read) markRead(notif._id); }}
                    >
                      {/* Severity indicator */}
                      <div className={`w-8 h-8 rounded-lg ${style.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Warning size={14} weight="fill" className={style.text} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`font-body text-xs leading-snug ${!notif.read ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}>
                            {notif.title}
                          </p>
                          {!notif.read && (
                            <span className={`w-2 h-2 rounded-full ${style.dot} flex-shrink-0 mt-1`} />
                          )}
                        </div>

                        <p className="font-body text-[11px] text-text-secondary mt-0.5 line-clamp-2">{notif.message}</p>

                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-body font-semibold uppercase tracking-wider ${style.bg} ${style.text}`}>
                            {notif.severity}
                          </span>
                          <span className="text-[10px] font-body text-tertiary">{notif.industry}</span>
                          <span className="text-border">|</span>
                          <span className="flex items-center gap-0.5 text-[10px] font-body text-tertiary">
                            <Clock size={10} />
                            {timeAgo(notif.created_at)}
                          </span>
                        </div>

                        {/* Transition badge */}
                        <div className="flex items-center gap-1 mt-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${notif.previous_status === 'green' ? 'bg-success' : 'bg-alert-warning'}`} />
                          <CaretRight size={8} className="text-tertiary" />
                          <span className={`w-1.5 h-1.5 rounded-full ${notif.new_status === 'red' ? 'bg-alert-critical' : 'bg-alert-warning'}`} />
                          <span className="text-[10px] font-body text-tertiary ml-0.5">
                            {notif.metric_label}: {notif.value}{notif.threshold ? ` (limit: ${notif.threshold})` : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
